import type { FailureBehavior } from "@/types/api";

/** What each action does, in the words the version page uses (AVI-176). */
export const ACTION_LABEL: Record<FailureBehavior, string> = {
  block: "Block",
  redact: "Redact",
  warn: "Warn",
  inject_correction: "Retry, then block",
  safe_fallback: "Block",
};

export const ACTION_HINT: Record<FailureBehavior, string> = {
  block: "The user gets the safe message and no answer.",
  redact: "The offending part is masked; the rest is shown.",
  warn: "Nothing changes for the user; the trigger is logged and flagged in the trace.",
  inject_correction: "The model is asked to fix it; if it can't, the user gets the safe message.",
  safe_fallback: "The user gets the safe message and no answer.",
};

/** The editable form of a check's config. Strings, so inputs stay controlled. */
export interface ConfigForm {
  maxChars: string;
  llmCheck: boolean;
  topics: string;
  declineMessage: string;
  restrictedFields: string;
  schema: string;
}

const lines = (v: unknown): string => (Array.isArray(v) ? v.map(String).join("\n") : "");

export function configToForm(config: Record<string, unknown> | null | undefined): ConfigForm {
  const c = config ?? {};
  return {
    maxChars: c.max_chars != null ? String(c.max_chars) : "",
    llmCheck: c.llm_check === true,
    topics: lines(c.allowed_topics),
    declineMessage: typeof c.decline_message === "string" ? c.decline_message : "",
    restrictedFields: lines(c.restricted_fields),
    schema: c.schema != null ? JSON.stringify(c.schema, null, 2) : "",
  };
}

const splitLines = (text: string): string[] =>
  text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

/** The config to save for ``slug``, or an error message for the form. */
export function formToConfig(
  slug: string,
  form: ConfigForm,
): { config: Record<string, unknown> } | { error: string } {
  switch (slug) {
    case "input-length": {
      const n = Number(form.maxChars);
      if (!Number.isInteger(n) || n < 1) return { error: "Enter a whole number of characters." };
      return { config: { max_chars: n } };
    }
    case "prompt-injection":
      return { config: { llm_check: form.llmCheck } };
    case "topic-scope":
      return {
        config: {
          allowed_topics: splitLines(form.topics),
          decline_message: form.declineMessage.trim(),
        },
      };
    case "data-leakage":
      return { config: { restricted_fields: splitLines(form.restrictedFields) } };
    case "output-format": {
      try {
        const schema: unknown = JSON.parse(form.schema || "{}");
        if (typeof schema !== "object" || schema === null || Array.isArray(schema)) {
          return { error: "The schema must be a JSON object." };
        }
        return { config: { schema } };
      } catch {
        return { error: "The schema is not valid JSON." };
      }
    }
    default:
      return { config: {} };
  }
}

/** Checks that have settings beyond the action. */
export const HAS_SETTINGS = new Set([
  "input-length",
  "prompt-injection",
  "topic-scope",
  "data-leakage",
  "output-format",
]);
